import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Card,
  CircularProgress,
  List,
  ListItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { auth, db } from '../../../firebase/config';
import { doc, onSnapshot, updateDoc, collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import {
  subscribeToMetaData,
  addCity,
  updateCity,
  deleteCity,
  subscribeToUserByEmail,
  fetchUsers,
  updateUser,
  deleteUser
} from '../../../Services/FirebaseUtils';
import axios from 'axios';

const ROLE_TYPES = [
  'admin',
  'sales-manager',
  'sales-user',
  'operations-user',
  'operations-manager',
  'broker'
];

const Settings = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    role: "",
  });
  const [userId, setUserId] = useState(null);
  const [metaData, setMetaData] = useState({ cities: [], micromarkets: [] });
  const [editingCity, setEditingCity] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMicromarketsDialogOpen, setIsMicromarketsDialogOpen] = useState(false);
  const [cityForm, setCityForm] = useState({
    name: '',
    state: '',
    micromarkets: []
  });
  const [newMicromarket, setNewMicromarket] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserEditDialogOpen, setIsUserEditDialogOpen] = useState(false);
  const [isUserDeleteDialogOpen, setIsUserDeleteDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    role: '',
    password: '' // Default password
  });
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingCity, setSavingCity] = useState(false);
  const [savingMicromarket, setSavingMicromarket] = useState(false);
  const [loadingMicromarkets, setLoadingMicromarkets] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [micromarketsList, setMicromarketsList] = useState([]);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [selectedActionUser, setSelectedActionUser] = useState(null);

  // Setup real-time listener for meta data
  useEffect(() => {
    const unsubscribe = subscribeToMetaData(
      (data) => setMetaData(data),
      (error) => {
        console.error("Error listening to meta data:", error);
        setSnackbar({
          open: true,
          message: 'Error listening to updates',
          severity: 'error'
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // Setup real-time listener for user data based on auth email
  useEffect(() => {
    if (!auth.currentUser?.email) {
      console.log("No authenticated user email found");
      return;
    }

    console.log("Looking for user with email:", auth.currentUser.email);

    const unsubscribe = subscribeToUserByEmail(
      auth.currentUser.email,
      (userData) => {
        console.log("Found user data:", userData);
        setUserId(userData.id);
        setUserDetails({
          name: userData.name || "",
          email: userData.email || "",
          role: userData.role || ""
        });
      },
      (error) => {
        console.error("Error fetching user:", error);
        setSnackbar({
          open: true,
          message: 'Error fetching user data',
          severity: 'error'
        });
      }
    );

    return () => unsubscribe();
  }, [auth.currentUser?.email]);

  // Subscribe to specific user document changes
  useEffect(() => {
    if (!userId) return;

    console.log("Subscribing to user document:", userId);
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          console.log("User document updated:", userData);
          setUserDetails({
            name: userData.name || "",
            email: userData.email || "",
            role: userData.role || ""
          });
        }
      },
      (error) => {
        console.error("Error watching user document:", error);
        setSnackbar({
          open: true,
          message: 'Error watching user data',
          severity: 'error'
        });
      }
    );

    return () => unsubscribe();
  }, [userId]);

  console.log("Current userDetails:", userDetails);

  const getMicromarketsForCity = (cityName) => {
    return metaData.micromarkets.filter(m => m.city === cityName);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleUserDetailsChange = (field) => (event) => {
    setUserDetails({
      ...userDetails,
      [field]: event.target.value,
    });
  };

  const handleMenuOpen = async (event, city) => {
    setSelectedCity(city);
    setMenuAnchorEl(event.currentTarget);
    setLoadingMicromarkets(true);
    try {
      const micromarkets = await getMicromarketsForCity(city);
      setMicromarketsList(micromarkets);
    } catch (error) {
      console.error('Error loading micromarkets:', error);
      setSnackbar({
        open: true,
        message: 'Error loading micromarkets',
        severity: 'error'
      });
    } finally {
      setLoadingMicromarkets(false);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditCity = () => {
    setEditingCity(selectedCity);
    setCityForm({
      name: selectedCity,
      state: '',
      micromarkets: getMicromarketsForCity(selectedCity).map(m => m.micromarket)
    });
    setIsDialogOpen(true);
    handleMenuClose();
  };

  const handleEditMicromarkets = () => {
    setEditingCity(selectedCity);
    setCityForm({
      name: selectedCity,
      state: '',
      micromarkets: getMicromarketsForCity(selectedCity).map(m => m.micromarket)
    });
    setIsMicromarketsDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleCityFormChange = (field) => (event) => {
    setCityForm({
      ...cityForm,
      [field]: event.target.value
    });
  };

  const handleAddMicromarket = () => {
    if (newMicromarket.trim()) {
      setCityForm(prev => ({
        ...prev,
        micromarkets: [...prev.micromarkets, newMicromarket.trim()]
      }));
      setNewMicromarket('');
    }
  };

  const handleRemoveMicromarket = (micromarket) => {
    setCityForm(prev => ({
      ...prev,
      micromarkets: prev.micromarkets.filter(m => m !== micromarket)
    }));
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCity(selectedCity);
      setSnackbar({
        open: true,
        message: 'City deleted successfully',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting city:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting city',
        severity: 'error'
      });
    }
  };

  const handleSaveCity = async () => {
    // Validation checks
    if (!cityForm.name || cityForm.name.trim() === '') {
      setSnackbar({
        open: true,
        message: 'City name cannot be empty',
        severity: 'error'
      });
      return;
    }

    // Validate city name format (only letters, spaces, and hyphens)
    const cityNameRegex = /^[a-zA-Z\s-]+$/;
    if (!cityNameRegex.test(cityForm.name.trim())) {
      setSnackbar({
        open: true,
        message: 'City name can only contain letters, spaces, and hyphens',
        severity: 'error'
      });
      return;
    }

    setSavingCity(true);
    try {
      if (editingCity) {
        await updateCity(editingCity, cityForm.name.trim(), cityForm.micromarkets);
      } else {
        await addCity(cityForm.name.trim(), cityForm.micromarkets);
      }
      setSnackbar({
        open: true,
        message: `City ${editingCity ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
      setIsDialogOpen(false);
      setIsMicromarketsDialogOpen(false);
    } catch (error) {
      console.error('Error saving city:', error);
      setSnackbar({
        open: true,
        message: `Error ${editingCity ? 'updating' : 'adding'} city`,
        severity: 'error'
      });
    } finally {
      setSavingCity(false);
    }
  };

  // Fetch users when component mounts
  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await fetchUsers();
      if (result.success) {
        setUsers(result.users);
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load users',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Edit user handler
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserEditDialogOpen(true);
  };

  // Update user handler
  const handleUpdateUser = async () => {
    setUpdatingUser(true);
    try {
      if (!selectedUser || !selectedUser.id) {
        setSnackbar({
          open: true,
          message: 'No user selected',
          severity: 'error'
        });
        return;
      }

      const result = await updateUser(selectedUser.id, {
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role
      });

      if (result.success) {
        // Close edit dialog
        setIsUserEditDialogOpen(false);

        // Reload users table
        await loadUsers();

        // Show success snackbar
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
      } else {
        // Show error snackbar
        setSnackbar({
          open: true,
          message: result.error || 'Failed to update user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setSnackbar({
        open: true,
        message: 'Error updating user',
        severity: 'error'
      });
    } finally {
      setUpdatingUser(false);
    }
  };

  // Delete user handler
  const handleDeleteUser = async (userId) => {
    try {
      const result = await deleteUser(userId);

      if (result.success) {
        // Reload users table
        await loadUsers();

        // Show success snackbar
        setSnackbar({
          open: true,
          message: 'User deleted successfully',
          severity: 'success'
        });
      } else {
        // Show error snackbar
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting user',
        severity: 'error'
      });
    }
  };

  const handleAddUser = async () => {
    setSavingUser(true);
    try {
      // Validate form
      if (!newUserForm.email || !newUserForm.password || !newUserForm.name || !newUserForm.role) {
        setSnackbar({
          open: true,
          message: 'Please fill in all fields',
          severity: 'error'
        });
        return;
      }

      // Call the backend API to create user
      //Create user URL
      let apiBaseUrl = "";
      if(process.env.REACT_APP_ENV === 'PROD') {
        apiBaseUrl = process.env.REACT_APP_PROD_FUNCTIONS_BASE_URL;
      } else {
        apiBaseUrl = process.env.REACT_APP_DEV_FUNCTIONS_BASE_URL;
      }
      // console.log('process.env.REACT_APP_ENV',process.env.REACT_APP_ENV,'API Base URL:', apiBaseUrl);
      const createUserServiceURL = `${apiBaseUrl}/createUser`;
      const response = await axios.post(createUserServiceURL, {
        email: newUserForm.email,
        password: newUserForm.password,
        name: newUserForm.name,
        role: newUserForm.role,
      });

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success'
        });
        setIsAddUserDialogOpen(false);
        setNewUserForm({
          name: '',
          email: '',
          password: '',
          role: 'user'
        });

        // Refresh users list
        await loadUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error creating user',
        severity: 'error'
      });
    } finally {
      setSavingUser(false);
    }
  };

  const handleUserMenuOpen = (event, user) => {
    setSelectedActionUser(user);
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
    setSelectedActionUser(null);
  };

  const handleUserEdit = () => {
    handleEditUser(selectedActionUser);
    handleUserMenuClose();
  };

  const handleUserDelete = () => {
    handleDeleteUser(selectedActionUser.id);
    handleUserMenuClose();
  };

  // Render users table
  const renderUsersTable = () => (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Users</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsAddUserDialogOpen(true)}
        >
          Add User
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>Name</TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>Email</TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>Role</TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: "#6B7280",
                    borderBottom: "1px solid #E5E7EB",
                    py: 1.5
                  }}>{user.name}</TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: "#6B7280",
                    borderBottom: "1px solid #E5E7EB",
                    py: 1.5
                  }}>{user.email}</TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: "#6B7280",
                    borderBottom: "1px solid #E5E7EB",
                    py: 1.5
                  }}>
                    <Chip
                      label={user.role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      color={
                        user.role === 'admin' ? 'error' :
                          user.role.includes('manager') ? 'primary' :
                            user.role.includes('user') ? 'secondary' :
                              'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: "#6B7280",
                    borderBottom: "1px solid #E5E7EB",
                    py: 1.5
                  }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleUserMenuOpen(e, user)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
      >
        <MenuItem onClick={handleUserEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUserDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog
        open={isAddUserDialogOpen}
        onClose={() => setIsAddUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={newUserForm.name}
              onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={newUserForm.email}
              onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUserForm.role}
                label="Role"
                onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
              >
                {ROLE_TYPES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Default Password"
              fullWidth
              type="text"
              value={newUserForm.password}
              onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setIsAddUserDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddUser}
            disabled={savingUser}
            startIcon={savingUser ? <CircularProgress size={20} /> : null}
          >
            {savingUser ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderCitiesContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ height: '100%', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
              <TableCell sx={{
                fontWeight: 500,
                color: "#6B7280",
                borderBottom: "1px solid #E5E7EB",
                py: 1.5
              }}>City Name</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                color: "#6B7280",
                borderBottom: "1px solid #E5E7EB",
                py: 1.5
              }}>State</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                color: "#6B7280",
                borderBottom: "1px solid #E5E7EB",
                py: 1.5
              }}>Micromarkets</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                color: "#6B7280",
                borderBottom: "1px solid #E5E7EB",
                py: 1.5
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {metaData.cities.map((city) => (
              <TableRow key={city} hover>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }} >{city}</TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>{city.state || '-'}</TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>
                  {getMicromarketsForCity(city).length || 0} micromarkets
                </TableCell>
                <TableCell sx={{
                  fontWeight: 500,
                  color: "#6B7280",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5
                }}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, city)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCitiesTab = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Cities</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingCity(null);
              setIsDialogOpen(true);
            }}
          >
            Add City
          </Button>
        </Box>
        {renderCitiesContent()}
      </Box>
    );
  };

  // Update tab rendering to include users tab
  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return renderUsersTable();
      case 1:
        return renderCitiesTab();
      default:
        return null;
    }
  };



  // Add users tab to existing tabs
  return (
    <Card sx={{ borderRadius: 1 }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="settings tabs">
            <Tab label="Users" />
            <Tab label="Cities" />
          </Tabs>
        </Box>
        {renderTabContent()}

        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditCity}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit City</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditMicromarkets}>
            <ListItemIcon>
              <LocationIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Micromarkets</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* City Edit Dialog */}
        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingCity ? 'Edit City' : 'Add New City'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="City Name"
                value={cityForm.name}
                onChange={handleCityFormChange('name')}
              />
              <TextField
                fullWidth
                label="State"
                value={cityForm.state}
                onChange={handleCityFormChange('state')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveCity} disabled={savingCity} startIcon={savingCity ? <CircularProgress size={20} /> : null}>
              {savingCity ? 'Saving...' : 'Save City'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Micromarkets Edit Dialog */}
        <Dialog
          open={isMicromarketsDialogOpen}
          onClose={() => setIsMicromarketsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Edit Micromarkets for {editingCity}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Micromarkets</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {cityForm.micromarkets.map((micromarket, index) => (
                  <Chip
                    key={index}
                    label={micromarket}
                    onDelete={() => handleRemoveMicromarket(micromarket)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Add Micromarket"
                  value={newMicromarket}
                  onChange={(e) => setNewMicromarket(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMicromarket();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddMicromarket}
                  disabled={savingMicromarket}
                  startIcon={savingMicromarket ? <CircularProgress size={20} /> : null}
                >
                  {savingMicromarket ? 'Adding...' : 'Add '}
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setIsMicromarketsDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveCity} disabled={savingCity} startIcon={savingCity ? <CircularProgress size={20} /> : null}>
              {savingCity ? 'Saving...' : 'Save Micromarkets'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete City</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedCity}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Edit Dialog */}
        <Dialog
          open={isUserEditDialogOpen}
          onClose={() => setIsUserEditDialogOpen(false)}
        >
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              value={selectedUser?.name || ''}
              onChange={(e) => setSelectedUser(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Email"
              fullWidth
              value={selectedUser?.email || ''}
              onChange={(e) => setSelectedUser(prev => ({ ...prev, email: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedUser?.role || ''}
                label="Role"
                onChange={(e) => setSelectedUser(prev => ({ ...prev, role: e.target.value }))}
              >
                {ROLE_TYPES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setIsUserEditDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary" onClick={handleUpdateUser} disabled={updatingUser} startIcon={updatingUser ? <CircularProgress size={20} /> : null}>
              {updatingUser ? 'Updating...' : 'Update User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Delete Confirmation Dialog */}
        <Dialog
          open={isUserDeleteDialogOpen}
          onClose={() => setIsUserDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the user {selectedUser?.name}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsUserDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => handleDeleteUser(selectedUser.id)} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Card>

  );
};

export default Settings;
